import { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import ReactPlayer from "react-player";
import { useSwiperSlide } from "swiper/react";

import ResponsiveImage from "components/ResponsiveImage";
import { hasAudio } from "utils/videoPlayerUtils";
import { getImageUrl } from "utils/imageHandlerUtils";

import VolumeButton from "../VolumeButton";

const MediaWrapper = styled("div")(({ background, bottomBackground }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: 10,
  background: bottomBackground ? `linear-gradient(to bottom, ${background}, ${bottomBackground})` : background,
}));

const MediaImage = styled(ResponsiveImage)(() => ({
  maxWidth: "100%",
  height: "100%",
  objectFit: "contain",
  objectPosition: "center",
}));

const MediaVideo = styled(ReactPlayer)(() => ({
  objectFit: "contain",
  objectPosition: "center",
}));

const Media = ({ video, image, title, background = "black", bottomBackground, onVideoDuration = () => {} }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [videoHasAudio, setVideoHasAudio] = useState(false);
  const videoRef = useRef(null);
  const onlyImage = image && !video;
  const swiperSlide = useSwiperSlide();

  const mute = (muteValue) => {
    setIsMuted(muteValue);
    if (videoRef.current) videoRef.current.muted = muteValue;
  };

  useEffect(() => {
    if (!swiperSlide?.isActive && videoRef.current) mute(true);
  }, [swiperSlide?.isActive]);

  useEffect(() => {
    if (videoRef.current && image) videoRef.current.poster = getImageUrl(image, { newWidth: 765 });
  }, [image]);

  const handleVolumeToggle = () => mute(!isMuted);

  const detectAudio = () => {
    if (videoRef.current) {
      setVideoHasAudio(hasAudio(videoRef.current));
    }
  };

  const mediaElement = useMemo(() => {
    if (onlyImage) return <MediaImage src={image} alt={title} width={765} widthMobile={353} />;
    if (video)
      return (
        <MediaVideo
          data-testid="media-video"
          ref={videoRef}
          playing={swiperSlide?.isActive}
          height="100%"
          width="100%"
          loop
          muted
          playsInline
          src={video}
          onPlaying={detectAudio}
          onDurationChange={(duration) => onVideoDuration(duration)}
        />
      );

    return null;
  }, [onlyImage, video, image, swiperSlide?.isActive]);

  if (!video && !image) return null;

  const showVolumeButton = swiperSlide?.isActive && video && videoHasAudio;

  return (
    <MediaWrapper background={background} bottomBackground={bottomBackground} data-testid="media-wrapper">
      {showVolumeButton ? <VolumeButton onMute={handleVolumeToggle} muted={isMuted} /> : null}
      {mediaElement}
    </MediaWrapper>
  );
};

Media.propTypes = {
  video: PropTypes.string,
  image: PropTypes.string.isRequired,
  title: PropTypes.string,
  background: PropTypes.string,
  bottomBackground: PropTypes.string,
  onVideoDuration: PropTypes.func,
};

export default Media;
